/**
 * 角色卡社区页（T07b）可见部件元数据。
 *
 * 本文件是「安卓移植清单」的数据源之一：页面里每个可以看到的部件都在这里声明
 * id / 角色 / 中文名 / 390x844 逻辑坐标 / zIndex / props / notes；页面组件通过
 * `useComponentMeta` 把同一份元数据登记进 `src/ui/registry.tsx`。运行时列表
 * （卡片、标签）按固定模板 + 实例 id 动态登记，见 `CommunityPage.tsx`。
 *
 * 坐标约定：与 App 外壳一致，一律是 390x844 画布上的绝对逻辑坐标
 * （x 向右、y 向下，左上角为原点），页面内容区从 y=24 的状态栏安全区之下开始。
 */
import type { ComponentMeta } from '../../core/types';
import type { BoxLayout, ComponentMetaInput } from '../../ui';

/** 本页在组件清单里的 page 标识。 */
export const PAGE_ID = 'community';

/** 页面内容区顶部的状态栏安全区偏移（画布 y）。 */
export const PAGE_TOP = 24;

interface MetaOptions {
  zIndex?: number;
  props?: string[];
  notes?: string;
}

/**
 * role → 安卓控件建议（T18 notes 补全）。
 *
 * 未显式写 `notes` 的部件用这张表兜底，保证清单里「每个可见部件都有一句安卓用什么控件 +
 * 关键属性」；显式写了 `notes` 的部件保持自己的更精确提示。
 * 关键属性统一是「按 390x844 锚点换算 dp 定位」与触摸目标下限。
 */
const ANDROID_CONTROL_BY_ROLE: Record<NonNullable<ComponentMetaInput['role']>, string> = {
  background: 'View 背景层（配 --color-bg / --color-overlay）',
  container: 'FrameLayout 或 ConstraintLayout 容器',
  button: 'MaterialButton（minHeight=44dp、圆角 radius-full）',
  input: 'TextInputEditText + TextInputLayout',
  text: 'TextView（按 390x844 锚点换算 dp 定位，单行时 ellipsize=end）',
  icon: 'ImageView 或 ShapeableImageView',
  list: 'RecyclerView（item 布局复用同一份锚点）',
  modal: 'Dialog 或 BottomSheetDialog（dim 遮罩层）',
  nav: 'BottomNavigationView',
};

/** 兜底移植提示：控件 + 关键属性（安卓最小触摸目标 44dp）。 */
function defaultNotes(label: string, role: NonNullable<ComponentMetaInput['role']>): string {
  return `${label}；安卓用 ${ANDROID_CONTROL_BY_ROLE[role]}；锚点即 390x844 画布下的 dp 定位，可点部件保持 ≥44dp 触摸目标`;
}

function meta(
  id: string,
  label: string,
  role: ComponentMetaInput['role'],
  layout: BoxLayout,
  options: MetaOptions = {},
): ComponentMetaInput {
  return {
    id,
    page: PAGE_ID,
    label,
    role,
    layout,
    zIndex: options.zIndex ?? 0,
    ...(options.props === undefined ? {} : { props: options.props }),
    notes: options.notes ?? defaultNotes(label, role ?? 'container'),
  };
}

/* -------------------------------------------------------------------------- *
 * 列表页布局
 * -------------------------------------------------------------------------- */

const ROOT: BoxLayout = { x: 0, y: PAGE_TOP, width: 390, height: 744 };
const HEADER: BoxLayout = { x: 0, y: 24, width: 390, height: 56 };
const HEADER_TITLE: BoxLayout = { x: 16, y: 36, width: 358, height: 32 };
const SEARCH: BoxLayout = { x: 16, y: 88, width: 358, height: 44 };
export const COMMUNITY_TAG_BAR_LAYOUT: BoxLayout = { x: 0, y: 140, width: 390, height: 44 };
const SOURCE_LABEL: BoxLayout = { x: 16, y: 190, width: 358, height: 16 };
const SOURCE_INPUT: BoxLayout = { x: 16, y: 210, width: 222, height: 44 };
const SOURCE_LOAD_BUTTON: BoxLayout = { x: 246, y: 210, width: 128, height: 44 };
const SOURCE_BUILTIN_BUTTON: BoxLayout = { x: 16, y: 258, width: 358, height: 44 };
const SOURCE_HINT: BoxLayout = { x: 16, y: 304, width: 358, height: 16 };
export const COMMUNITY_LIST_LAYOUT: BoxLayout = { x: 0, y: 326, width: 390, height: 442 };

const STATE: BoxLayout = { x: 0, y: 384, width: 390, height: 160 };
const STATE_TITLE: BoxLayout = { x: 0, y: 412, width: 390, height: 32 };
const STATE_HINT: BoxLayout = { x: 0, y: 448, width: 390, height: 24 };
const STATE_RETRY: BoxLayout = { x: 123, y: 486, width: 144, height: 44 };

/** 列表项行高（dp）。 */
export const COMMUNITY_CARD_ITEM_HEIGHT = 108;

/** 第 index 张社区卡片的画布布局。 */
export function communityItemLayout(index: number): BoxLayout {
  return {
    x: 0,
    y: COMMUNITY_LIST_LAYOUT.y + index * COMMUNITY_CARD_ITEM_HEIGHT,
    width: 390,
    height: COMMUNITY_CARD_ITEM_HEIGHT,
  };
}

const CARD_PART_OFFSETS = {
  avatar: { x: 16, y: 14, width: 44, height: 44 },
  name: { x: 72, y: 14, width: 220, height: 24 },
  author: { x: 298, y: 16, width: 76, height: 20 },
  description: { x: 72, y: 42, width: 300, height: 20 },
  tags: { x: 72, y: 68, width: 300, height: 22 },
} as const;

export type CommunityCardPart = keyof typeof CARD_PART_OFFSETS;

/** 卡片内部部件的画布布局。 */
export function communityItemPartLayout(item: BoxLayout, part: CommunityCardPart): BoxLayout {
  const offset = CARD_PART_OFFSETS[part];
  return {
    x: item.x + offset.x,
    y: item.y + offset.y,
    width: offset.width,
    height: offset.height,
  };
}

/** 第 index 个标签筛选胶囊的画布布局（横向滚动，宽高固定）。 */
export function communityTagLayout(index: number): BoxLayout {
  return { x: 16 + index * 96, y: COMMUNITY_TAG_BAR_LAYOUT.y, width: 88, height: 44 };
}

/* -------------------------------------------------------------------------- *
 * 详情预览页布局（只读）
 * -------------------------------------------------------------------------- */

const DETAIL_ROOT: BoxLayout = { x: 0, y: PAGE_TOP, width: 390, height: 744 };
const DETAIL_HEADER: BoxLayout = { x: 0, y: 24, width: 390, height: 56 };
const DETAIL_BACK: BoxLayout = { x: 12, y: 32, width: 44, height: 44 };
const DETAIL_TITLE: BoxLayout = { x: 64, y: 38, width: 310, height: 32 };
const DETAIL_AVATAR: BoxLayout = { x: 16, y: 96, width: 64, height: 64 };
const DETAIL_NAME: BoxLayout = { x: 92, y: 100, width: 282, height: 28 };
const DETAIL_AUTHOR: BoxLayout = { x: 92, y: 132, width: 282, height: 22 };
const DETAIL_TAGS: BoxLayout = { x: 16, y: 176, width: 358, height: 28 };
const DETAIL_PROMPT_LABEL: BoxLayout = { x: 16, y: 220, width: 358, height: 18 };
const DETAIL_PROMPT: BoxLayout = { x: 16, y: 240, width: 358, height: 170 };
const DETAIL_PROMPT_MORE: BoxLayout = { x: 16, y: 414, width: 358, height: 18 };
const DETAIL_WORLDBOOK: BoxLayout = { x: 16, y: 440, width: 358, height: 44 };
const DETAIL_STATUS: BoxLayout = { x: 16, y: 646, width: 358, height: 20 };
const DETAIL_CANCEL: BoxLayout = { x: 16, y: 684, width: 174, height: 48 };
const DETAIL_IMPORT: BoxLayout = { x: 200, y: 684, width: 174, height: 48 };

/* -------------------------------------------------------------------------- *
 * 冲突确认弹层布局
 * -------------------------------------------------------------------------- */

const CONFLICT_BACKDROP: BoxLayout = { x: 0, y: PAGE_TOP, width: 390, height: 744 };
const CONFLICT_DIALOG: BoxLayout = { x: 30, y: 300, width: 330, height: 232 };
const CONFLICT_TITLE: BoxLayout = { x: 50, y: 318, width: 290, height: 28 };
const CONFLICT_MESSAGE: BoxLayout = { x: 50, y: 352, width: 290, height: 64 };
const CONFLICT_OVERWRITE: BoxLayout = { x: 50, y: 428, width: 140, height: 44 };
const CONFLICT_RENAME: BoxLayout = { x: 200, y: 428, width: 140, height: 44 };
const CONFLICT_CANCEL: BoxLayout = { x: 50, y: 484, width: 290, height: 44 };

/* -------------------------------------------------------------------------- *
 * 静态部件清单
 * -------------------------------------------------------------------------- */

const LIST_COMPONENTS: ComponentMetaInput[] = [
  meta('page.community.root', '栀歆角色卡页', 'container', ROOT, {
    zIndex: 1,
    notes: '社区页根容器（390x844，内容区 y=24 起），安卓用 Fragment',
  }),
  meta('community.header', '顶部标题栏', 'container', HEADER, { zIndex: 2 }),
  meta('community.header.title', '页面标题', 'text', HEADER_TITLE, { zIndex: 3 }),
  meta('community.searchInput', '关键字搜索框', 'input', SEARCH, {
    zIndex: 3,
    props: ['value', 'placeholder'],
    notes: '按名称 / 简介 / 标签模糊搜索，安卓用 EditText + TextWatcher',
  }),
  meta('community.tagBar', '标签筛选栏', 'list', COMMUNITY_TAG_BAR_LAYOUT, {
    zIndex: 3,
    props: ['selectedTag'],
    notes: '横向滚动标签胶囊；点击切换筛选，安卓用 ChipGroup + HorizontalScrollView',
  }),
  meta('community.tag', '标签筛选胶囊', 'button', communityTagLayout(0), {
    zIndex: 4,
    props: ['tag', 'selected'],
    notes: '运行时按标签实例化（community.tag.<tag>），触摸目标 88x44',
  }),
  meta('community.sourceLabel', '远端来源说明', 'text', SOURCE_LABEL, { zIndex: 3 }),
  meta('community.sourceInput', '远端目录地址输入', 'input', SOURCE_INPUT, {
    zIndex: 3,
    props: ['value', 'placeholder'],
    notes: '填入返回 JSON 的远端目录基址，安卓用 EditText(inputType=textUri)',
  }),
  meta('community.loadRemoteButton', '加载远端目录', 'button', SOURCE_LOAD_BUTTON, {
    zIndex: 3,
    props: ['disabled', 'loading'],
    notes: '按约定端点 GET {baseUrl}/cards 拉取远端目录',
  }),
  meta('community.builtinButton', '切回内置目录', 'button', SOURCE_BUILTIN_BUTTON, {
    zIndex: 3,
    props: ['pressed'],
    notes: '把来源切回随包内置目录',
  }),
  meta('community.sourceHint', '当前来源提示', 'text', SOURCE_HINT, {
    zIndex: 3,
    notes: '显示当前来源与卡片数量 / 错误提示',
  }),
  meta('community.list', '栀歆角色卡列表', 'list', COMMUNITY_LIST_LAYOUT, {
    zIndex: 3,
    notes: '纵向滚动卡片列表，安卓用 RecyclerView',
  }),
  meta('community.card', '栀歆角色卡卡片', 'button', communityItemLayout(0), {
    zIndex: 4,
    props: ['name', 'author', 'tags'],
    notes: '运行时卡片项（community.card.<id>），点击进入详情预览',
  }),
  meta('community.card.avatar', '卡片头像', 'icon', communityItemPartLayout(communityItemLayout(0), 'avatar'), {
    zIndex: 5,
  }),
  meta('community.card.name', '卡片名称', 'text', communityItemPartLayout(communityItemLayout(0), 'name'), {
    zIndex: 5,
  }),
  meta('community.card.author', '卡片作者', 'text', communityItemPartLayout(communityItemLayout(0), 'author'), {
    zIndex: 5,
  }),
  meta(
    'community.card.description',
    '卡片简介',
    'text',
    communityItemPartLayout(communityItemLayout(0), 'description'),
    { zIndex: 5 },
  ),
  meta('community.card.tags', '卡片标签', 'text', communityItemPartLayout(communityItemLayout(0), 'tags'), {
    zIndex: 5,
  }),
  meta('community.state.loading', '加载中状态', 'container', STATE, { zIndex: 6 }),
  meta('community.state.loading.text', '加载中提示', 'text', STATE_TITLE, { zIndex: 7 }),
  meta('community.state.empty', '空结果状态', 'container', STATE, { zIndex: 6 }),
  meta('community.state.empty.text', '空结果提示', 'text', STATE_TITLE, { zIndex: 7 }),
  meta('community.state.error', '加载失败状态', 'container', STATE, { zIndex: 6 }),
  meta('community.state.error.text', '失败提示', 'text', STATE_HINT, { zIndex: 7 }),
  meta('community.state.retryButton', '重试加载', 'button', STATE_RETRY, {
    zIndex: 7,
    props: ['pressed'],
    notes: '加载失败后重新请求当前来源',
  }),
];

const DETAIL_COMPONENTS: ComponentMetaInput[] = [
  meta('page.community.detail', '角色卡详情预览页', 'container', DETAIL_ROOT, {
    zIndex: 1,
    notes: '只读预览；安卓可复用同一 Fragment 或 BottomSheet',
  }),
  meta('community.detail.header', '详情顶部栏', 'container', DETAIL_HEADER, { zIndex: 2 }),
  meta('community.detail.backButton', '返回列表', 'button', DETAIL_BACK, {
    zIndex: 3,
    props: ['pressed'],
    notes: '返回栀歆角色卡列表',
  }),
  meta('community.detail.title', '详情标题', 'text', DETAIL_TITLE, { zIndex: 3 }),
  meta('community.detail.avatar', '角色头像', 'icon', DETAIL_AVATAR, { zIndex: 3 }),
  meta('community.detail.name', '角色名称', 'text', DETAIL_NAME, { zIndex: 3 }),
  meta('community.detail.author', '作者信息', 'text', DETAIL_AUTHOR, { zIndex: 3 }),
  meta('community.detail.tags', '标签行', 'text', DETAIL_TAGS, { zIndex: 3 }),
  meta('community.detail.promptLabel', '系统提示词标题', 'text', DETAIL_PROMPT_LABEL, { zIndex: 3 }),
  meta('community.detail.prompt', '系统提示词摘要', 'text', DETAIL_PROMPT, {
    zIndex: 3,
    notes: '只读；超出部分截断展示',
  }),
  meta('community.detail.promptMore', '提示词截断说明', 'text', DETAIL_PROMPT_MORE, { zIndex: 3 }),
  meta('community.detail.worldBook', '世界书条目数', 'text', DETAIL_WORLDBOOK, { zIndex: 3 }),
  meta('community.detail.status', '导入结果提示', 'text', DETAIL_STATUS, { zIndex: 3 }),
  meta('community.detail.cancelButton', '取消返回', 'button', DETAIL_CANCEL, {
    zIndex: 3,
    props: ['pressed'],
    notes: '不导入，返回列表',
  }),
  meta('community.detail.importButton', '导入到我的角色卡', 'button', DETAIL_IMPORT, {
    zIndex: 3,
    props: ['disabled', 'loading'],
    notes: 'fetchCard 后以 source=community 写入 presetRepo；id 冲突时弹二次确认',
  }),
];

const CONFLICT_COMPONENTS: ComponentMetaInput[] = [
  meta('community.conflict.backdrop', '冲突弹层遮罩', 'modal', CONFLICT_BACKDROP, {
    zIndex: 30,
    notes: '半透明遮罩，点击不关闭（避免误操作）',
  }),
  meta('community.conflict.dialog', '导入冲突确认框', 'modal', CONFLICT_DIALOG, { zIndex: 31 }),
  meta('community.conflict.title', '冲突标题', 'text', CONFLICT_TITLE, { zIndex: 32 }),
  meta('community.conflict.message', '冲突说明', 'text', CONFLICT_MESSAGE, { zIndex: 32 }),
  meta('community.conflict.overwriteButton', '覆盖导入', 'button', CONFLICT_OVERWRITE, {
    zIndex: 32,
    props: ['pressed'],
    notes: '用社区版本覆盖同 id 的本地角色卡',
  }),
  meta('community.conflict.renameButton', '改名导入', 'button', CONFLICT_RENAME, {
    zIndex: 32,
    props: ['pressed'],
    notes: '生成新 id 并以新名字导入，保留本地版本',
  }),
  meta('community.conflict.cancelButton', '取消导入', 'button', CONFLICT_CANCEL, {
    zIndex: 32,
    props: ['pressed'],
    notes: '放弃导入，关闭弹层',
  }),
];

/** 本页全部静态部件元数据。 */
export const COMMUNITY_COMPONENTS: ComponentMetaInput[] = [
  ...LIST_COMPONENTS,
  ...DETAIL_COMPONENTS,
  ...CONFLICT_COMPONENTS,
];

/** 供测试断言默认坐标的完整 ComponentMeta 列表。 */
export function communityComponentMetaList(): ComponentMeta[] {
  return COMMUNITY_COMPONENTS.map((input) => ({
    id: input.id,
    page: input.page,
    role: input.role ?? 'container',
    label: input.label,
    anchor: { frameWidth: 390, frameHeight: 844, ...input.layout },
    zIndex: input.zIndex ?? 0,
    ...(input.props === undefined ? {} : { props: [...input.props] }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }));
}

const META_BY_ID = new Map(COMMUNITY_COMPONENTS.map((entry) => [entry.id, entry]));

/** 按 id 读取本页静态部件元数据（页面渲染与清单共用同一份定义）。 */
export function communityMetaById(id: string): ComponentMetaInput {
  const found = META_BY_ID.get(id);
  if (found === undefined) throw new Error(`[community] 未知部件 id：${id}`);
  return found;
}
